<template>
  <BaseModalEditCreate
    @dismissChanges="onDiscardChanges"
    @save="onSave"
    maxWidth="extra"
    :isModified="isModified"
    :hide-save-button="mode == 'view'"
    :title="title">
    <template #form>
      <div class="relative h-full pb-4 overflow-x-auto bg-white">
        <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
          <tbody>
            <BaseDetailsTableRow :name="$t('device-configuration.functionality')" :mode="mode">
              <BaseSelectDropdown
                :items="dropdownFunctionalities"
                :selected-item="selectedFunctionality"
                @update:selected="handleFunctionalitySelection"
                name="functionality"
                :mode="mode"
                clearable
                show-error-as-tooltip
                :error="
                  $v.functionality.required.$invalid ? $v.functionality.required.$message : ''
                " />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('baseObject.code')" :mode="mode">
              <BaseInput
                :mode="mode"
                v-model="modifiedData.code"
                color="base"
                flat
                :disabled="mode == 'view'" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('baseObject.name')" :mode="mode">
              <BaseInput
                :mode="mode"
                v-model="modifiedData.name"
                color="base"
                flat
                :disabled="mode == 'view'" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('device-configuration.defaultUser')" :mode="mode">
              <BaseSelectDropdown
                :items="dropdownUsers"
                :selected-item="selectedUser"
                @update:selected="handleUserSelection"
                name="user"
                clearable
                :mode="mode" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('device-configuration.documentType')" :mode="mode">
              <BaseSelectDropdown
                :items="dropdownDocumentTypes"
                :selected-item="selectedDocumentType"
                @update:selected="handleDocumentTypeSelection"
                name="documentType"
                :mode="mode"
                clearable
                show-error-as-tooltip
                :error="
                  $v.documentTypeId.required.$invalid ? $v.documentTypeId.required.$message : ''
                " />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('device-configuration.documentSeries')" :mode="mode">
              <BaseSelectDropdown
                :items="dropdownDocumentSeries"
                :selected-item="selectedDocumentSeries"
                @update:selected="handleDocumentSeriesSelection"
                name="documentSeries"
                :mode="mode"
                show-error-as-tooltip
                clearable
                :error="
                  $v.documentSeriesId.required.$invalid ? $v.documentSeriesId.required.$message : ''
                " />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('device-configuration.companySite')" :mode="mode">
              <BaseSelectDropdown
                :items="dropdownCompanySites"
                :selected-item="selectedCompanySite"
                @update:selected="handleCompanySiteSelection"
                name="companySites"
                :mode="mode"
                show-error-as-tooltip
                clearable
                :error="
                  $v.companySiteId.required.$invalid ? $v.companySiteId.required.$message : ''
                " />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('baseObject.isActive')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="modifiedData.isActive ? modifiedData.isActive : 0" />
              <BaseToggle v-else name="isActiveDevConf" v-model="modifiedData.isActive" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('baseObject.deleted')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="modifiedData.deleted ? modifiedData.deleted : 0"
                :textTrue="$t('common.actions.yes')"
                :textFalse="$t('common.actions.no')" />
              <BaseToggle v-else name="deleted" v-model="modifiedData.deleted" />
            </BaseDetailsTableRow>
          </tbody>
        </table>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import type { SelectDropdownItem } from '@/types/Base';
import { eFunctionality, type DeviceConfiguration } from '@/types/DeviceConfiguration';
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { enumToDropdownItems } from '@/utils/enums';
import { getDocumentSeriesTypes } from '@/services/api/documents';
import { deepCopy } from '@/utils/data';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';

const props = defineProps<{
  mode: 'view' | 'edit';
  existingConfigurations?: DeviceConfiguration[];
  deviceConfiguration: DeviceConfiguration;
  title: string;
}>();

const emit = defineEmits(['save', 'close']);

const isModified = ref<boolean>(false);
const modifiedData = ref<DeviceConfiguration>(
  deepCopy<DeviceConfiguration>(props.deviceConfiguration)
);

const { dropdownUsers, dropdownDocumentTypes, dropdownCompanySites } =
  storeToRefs(useLookupsStore());

const { reloadSingleLookup } = useLookupsStore();

const dropdownFunctionalities = ref(enumToDropdownItems(eFunctionality));

const selectedFunctionality = ref<SelectDropdownItem | null>(null);
const selectedUser = ref<SelectDropdownItem | null>(null);
const selectedDocumentType = ref<SelectDropdownItem | null>(null);
const selectedDocumentSeries = ref<SelectDropdownItem | null>(null);
const selectedCompanySite = ref<SelectDropdownItem | null>(null);

const dropdownDocumentSeries = ref<SelectDropdownItem[]>([]);

async function getDocumentSeriesByDocumentType(documentTypeId: string) {
  const res = await getDocumentSeriesTypes(documentTypeId);
  if (res) {
    dropdownDocumentSeries.value = res
      .filter((item) => item.documentType?.id === documentTypeId)
      .map((item) => ({
        value: item.documentSeries?.id || '',
        name: item.documentSeries?.name || '',
      }));
  }
}

function handleFunctionalitySelection(item: SelectDropdownItem) {
  selectedFunctionality.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.functionality = item ? +item.value : null;
    modifiedData.value.functionalityString = item ? item.name : '';
  }
}

function handleUserSelection(item: SelectDropdownItem) {
  selectedUser.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.defaultUserID = item ? item.value.toString() : '';
  }
}

async function handleDocumentTypeSelection(item: SelectDropdownItem) {
  selectedDocumentType.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.documentTypeID = item ? item.value.toString() : '';
  }
  loadDocumentSeriesDropDown();
}

function handleDocumentSeriesSelection(item: SelectDropdownItem) {
  selectedDocumentSeries.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.documentSeriesID = item ? item.value.toString() : '';
  }
}

function handleCompanySiteSelection(item: SelectDropdownItem) {
  selectedCompanySite.value = item ? item : null;
  if (modifiedData.value) {
    modifiedData.value.companySiteID = item ? item.value.toString() : '';
  }
}

async function initDropdowns() {
  if (modifiedData.value) {
    // first get the functionality and then filter the priceCollection out.
    const functionalityValue = dropdownFunctionalities.value.find(
      (item) => item.value == modifiedData.value?.functionality
    );
    if (functionalityValue) {
      selectedFunctionality.value = functionalityValue;
      modifiedData.value.functionality = +functionalityValue.value;
      modifiedData.value.functionalityString = functionalityValue.name;
    }

    if (props.existingConfigurations) {
      // I want to use PriceCollection functionality only once
      const priceCollectionUsed = !!props.existingConfigurations.find(
        (x) => x.functionality == eFunctionality.PriceCollection
      );
      if (priceCollectionUsed) {
        dropdownFunctionalities.value = dropdownFunctionalities.value.filter(
          (item) => (item.value as number) != eFunctionality.PriceCollection
        );
      }
    }

    const userValue = dropdownUsers.value.find(
      (item) => item.value == modifiedData.value?.defaultUserID
    );
    if (userValue) selectedUser.value = userValue;

    const documentTypeValue = dropdownDocumentTypes.value.find(
      (item) => item.value == modifiedData.value?.documentTypeID
    );
    if (documentTypeValue) selectedDocumentType.value = documentTypeValue;

    loadDocumentSeriesDropDown();

    await reloadSingleLookup('companySites', true);
    if (modifiedData.value) {
      selectedCompanySite.value =
        dropdownCompanySites.value.find((x) => x.value === modifiedData.value.companySiteID) ||
        null;
    }
  }
}

async function loadDocumentSeriesDropDown() {
  selectedDocumentSeries.value = null;

  if (selectedDocumentType.value) {
    await getDocumentSeriesByDocumentType(selectedDocumentType.value.value.toString());
    if (dropdownDocumentSeries.value) {
      selectedDocumentSeries.value =
        dropdownDocumentSeries.value.find(
          (item) => item.value == modifiedData.value?.documentSeriesID
        ) || null;
    }
  }
  if (modifiedData.value) {
    modifiedData.value.documentSeriesID = selectedDocumentSeries.value?.value.toString() || '';
  }
}

onMounted(async () => {
  await initDropdowns();
});

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  emit('save', modifiedData.value);
}

function onDiscardChanges() {
  modifiedData.value = deepCopy<DeviceConfiguration>(props.deviceConfiguration);
  emit('close');
}

const validationModel = ref({
  deviceId: modifiedData.value?.deviceID || '',
  documentTypeId: modifiedData.value?.documentTypeID || '',
  documentSeriesId: modifiedData.value?.documentSeriesID || '',
  functionality: modifiedData.value?.functionality ?? '',
  companySiteId: modifiedData.value?.companySiteID ?? '',
});

const rules = computed(() => ({
  deviceId: { required: helpers.withMessage('Device is required', required) },
  documentTypeId: { required: helpers.withMessage('Document Type is required', required) },
  documentSeriesId: { required: helpers.withMessage('Document Series is required', required) },
  functionality: { required: helpers.withMessage('Functionality is required', required) },
  companySiteId: { required: helpers.withMessage('Company Site is required', required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.functionality = newData.functionality ?? '';
    validationModel.value.deviceId = newData.deviceID || '';
    validationModel.value.documentTypeId = newData.documentTypeID || '';
    validationModel.value.documentSeriesId = newData.documentSeriesID || '';
    validationModel.value.companySiteId = newData.companySiteID || '';
    isModified.value = JSON.stringify(newData) !== JSON.stringify(props.deviceConfiguration);
  },
  { deep: true }
);
</script>
